import type { ReactElement, ReactNode } from "react";
import { Children, isValidElement } from "react";
import { Route } from "react-router-dom";
import { ContactDetailScreen, ContactsScreen } from "../features/contacts";
import { SegmentDetailScreen, SegmentsScreen } from "../features/segments";
import { TemplateDetailScreen, TemplatesScreen } from "../features/templates";
import { CampaignDetailScreen, CampaignListScreen } from "../features/campaigns";
import { TelemetryScreen } from "../features/telemetry";
import { CampaignDetailSubrouteRedirect } from "./CampaignDetailSubrouteRedirect";
import { OverviewScreen } from "../features/overview";
import { OperationalHealthScreen } from "../features/operational-health";
import { AppShell } from "./AppShell";
import { NotFoundRoute } from "./NotFoundRoute";

/**
 * A migrated route gets `.panel-page` (design.md §9 step 3). `legacyPage`
 * (the `.legacy-panel-page` wrapper, task 5.2) was removed here in
 * redesign-panel-hubspot-mailgun, PR 14 slice C: `/plantillas` was the last
 * route still using it, and promoting it to `.panel-page` left `legacyPage`
 * with zero callers. The `.legacy-panel-page`/`base.css` STYLESHEET
 * selectors themselves are untouched — deleting those still requires the
 * full regression sweep design.md §9 step 5 reserves for its own dedicated
 * PR 17 cleanup slice; removing this now-dead React helper is ordinary
 * hygiene forced by this slice's own edit, not that cleanup.
 */
function migratedPage(screen: ReactNode): ReactNode {
  return <div className="panel-page">{screen}</div>;
}

/**
 * Canonical route map (design.md §3): `/` becomes the overview while every
 * existing route stays valid at its current path. `AppShell` is the layout
 * route; every destination renders inside its `<Outlet>` via `main#main-content`.
 *
 * Deliberately declarative (`<Route>` elements consumed by `<Routes>`), the
 * same shape as before PR5 — NOT `createBrowserRouter`'s data router. A data
 * router's internal navigation pipeline constructs a `fetch` `Request` for
 * every navigation (even with no route `loader`), and in this repo's current
 * Vitest + jsdom + Node test toolchain that crashes with `RequestInit:
 * Expected signal ("AbortSignal {}") to be an instance of AbortSignal` — a
 * genuine jsdom/undici `AbortSignal` class-identity mismatch, reproduced
 * independently of react-router-dom with a bare
 * `new Request(url, { signal: new AbortController().signal })`. It would
 * work in a real browser (a single engine provides both classes there), but
 * would leave every navigation test in this repo unable to run. The
 * unsaved-changes blocker (`state/routeState.ts#useUnsavedChangesBlocker`)
 * is built without `useBlocker` for the same reason.
 *
 * PR8 chain note (slice 8/10, final routing step): the nested
 * `campanas/:campaignId` detail route now has all three tab children —
 * Resumen, Monitoreo, and Telemetría (this slice) — completing design.md
 * §3's canonical route map for campaigns.
 */
export const routeElements = (
  <Route path="/" element={<AppShell />}>
    <Route index element={migratedPage(<OverviewScreen />)} />
    <Route path="contactos" element={migratedPage(<ContactsScreen />)} />
    <Route path="contactos/:contactId" element={migratedPage(<ContactDetailScreen />)} />
    {/*
      redesign-panel-hubspot-mailgun, PR 13 (task 13.2, design.md §5.4):
      Segments migrated onto the shared v2 primitives — mounts under
      `.panel-page` like Contacts/Overview/Campaigns/Telemetry/Operational
      health. `segmentos/:segmentId` is a directly addressable detail route
      (task 13.1 "direct detail route"), replacing the previous internal-state
      detail panel.
    */}
    <Route path="segmentos" element={migratedPage(<SegmentsScreen />)} />
    <Route path="segmentos/:segmentId" element={migratedPage(<SegmentDetailScreen />)} />
    {/*
      redesign-panel-hubspot-mailgun, PR 14 slice C (task 14.2, design.md
      §9 step 3): `plantillas` promoted to `.panel-page` now that
      `styles/features/templates.css` ships the real `DataTable`/
      `RecordList` responsive toggle — slice A deliberately kept this
      `legacyPage` to avoid the dual-render bug Codex caught on PR #147.
    */}
    <Route path="plantillas" element={migratedPage(<TemplatesScreen />)} />
    <Route path="plantillas/:templateId" element={migratedPage(<TemplateDetailScreen />)} />
    <Route path="campanas" element={migratedPage(<CampaignListScreen />)} />
    {/*
      task 8.2 / panel-redesign-v3 T5: campaign detail (design.md §3
      canonical route map: `/campanas/:campaignId`). A record-addressed
      detail route, not a top-level nav destination — deliberately excluded
      from `navigation.ts`'s `NAV_GROUPS`/`MOBILE_BOTTOM_DESTINATIONS` and
      from `navigationRouteParity.test.ts`'s parity check via its own
      documented `ROUTES_WITHOUT_NAV_ENTRY` exclusion (same treatment as the
      `*` catch-all, for the same reason: not every reachable route is a nav
      destination). REDISENO-PANEL.md §1 item 3: no longer a 3-tab shell with
      nested children — ONE page; its former Monitoreo/Telemetría tab
      sub-routes redirect here (below).
    */}
    <Route path="campanas/:campaignId" element={migratedPage(<CampaignDetailScreen />)} />
    <Route path="campanas/:campaignId/monitoreo" element={<CampaignDetailSubrouteRedirect />} />
    <Route path="campanas/:campaignId/telemetria" element={<CampaignDetailSubrouteRedirect />} />
    {/*
      redesign-panel-hubspot-mailgun, PR 10 (task 10.6): TelemetryScreen is
      migrated onto the shared v2 primitives/`TelemetryEvidenceMatrix` — it
      now mounts under `.panel-page` (not `.legacy-panel-page`) so
      `styles/features/telemetry.css`'s `.panel-page`-scoped rules apply,
      same convention as `overview.css`/`campaigns.css`.
    */}
    <Route path="telemetria" element={migratedPage(<TelemetryScreen />)} />
    {/*
      redesign-panel-hubspot-mailgun, PR 11 (task 11.7, design.md §3 +
      §5.7): "Configuración" / Estado operativo — the `sistema` nav group's
      first real destination (previously registered with zero destinations,
      PR5 scope note).
    */}
    <Route path="configuracion" element={migratedPage(<OperationalHealthScreen />)} />
    <Route path="*" element={<NotFoundRoute />} />
  </Route>
);

/** One registered route's absolute path, or `"*"` for the catch-all not-found route. */
export interface RegisteredRoute {
  readonly path: string;
  readonly isCatchAll: boolean;
}

/**
 * Walks `routeElements`'s children and returns each registered route's
 * absolute path (task 5.x review fix P2 — Jose Luis Arcila, PR #94:
 * `navigation.ts` and `routes.tsx` are two independently-maintained lists
 * that could silently drift). Reads the actual `<Route>` element tree
 * directly rather than duplicating a second manifest, so this can never
 * itself drift from what `<Routes>` really registers.
 */
export function extractRegisteredPaths(): RegisteredRoute[] {
  const root = routeElements as ReactElement<{ children?: ReactNode }>;
  const results: RegisteredRoute[] = [];

  Children.forEach(root.props.children, (child) => {
    if (!isValidElement(child)) {
      return;
    }
    const props = child.props as { index?: boolean; path?: string };
    if (props.index) {
      results.push({ path: "/", isCatchAll: false });
    } else if (props.path === "*") {
      results.push({ path: "*", isCatchAll: true });
    } else if (props.path) {
      results.push({ path: `/${props.path}`, isCatchAll: false });
    }
  });

  return results;
}
